import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Stack,
  Box,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { X } from '@phosphor-icons/react';
import { useParams } from 'react-router';
import { Api, useApiFormSubmit } from '../../api';
import { useForm } from 'react-typed-form';
import { toast } from 'sonner';
import { useInvalidation } from 'api-read-hook';
import { SoloLoading } from '..';

type Props = {
  open?: boolean;
  onClose?: () => void;
  userId?: string;
};

export default function EditMetricsModal({
  open = true,
  onClose,
  userId,
}: Props) {
  const params = useParams();
  const id = userId || params.id || '';
  if (!id) return null;
  return <Modal id={id} open={open} onClose={onClose} />;
}

function Modal({
  id,
  open = true,
  onClose,
}: {
  id: string;
  open?: boolean;
  onClose?: () => void;
}) {
  const metricSettingsApi = Api.UserMetricSetting.useReadItemMetricSettings(id);

  if (metricSettingsApi.error) return null;

  if (!metricSettingsApi.data) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6">Edit metric trackers</Typography>
          <IconButton onClick={onClose} size="small">
            <X size={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <SoloLoading />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <ModalLoaded
      open={open}
      onClose={onClose}
      id={id}
      existing={metricSettingsApi.data}
    />
  );
}

function ModalLoaded({
  id,
  open = true,
  onClose,
  existing,
}: {
  id: string;
  open?: boolean;
  onClose?: () => void;
  existing: Api.UserMetricSetting.V;
}) {
  const { invalidateExact } = useInvalidation();

  const form = useForm<Api.UserMetricSetting.W>({
    defaultValues: {
      setByUser: true,
      mood: existing.mood,
      cycleManual: !!existing.cycleManual,
      symptoms: existing.symptoms,
      dailyThoughts: existing.dailyThoughts,
      steps: existing.steps,
      exercise: existing.exercise,
      sleep: existing.sleep,
      food: existing.food || existing.water,
      water: existing.water || existing.food,
    },
    onSubmit: useApiFormSubmit<Api.UserMetricSetting.W>(
      'PUT',
      `/users/${id}/metric-settings`,
      {
        onSuccess: () => {
          invalidateExact(`/users/${id}/metric-settings`);
          invalidateExact(`/users/${id}/stats`);
          toast.success('Metrics updated');
          onClose?.();
        },
      },
    ),
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h6">Edit metric trackers</Typography>
        <IconButton onClick={onClose} size="small">
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Please be aware that this changes which trackers the user sees in the
          app. However, users have the ability to overwrite your changes.
        </Typography>

        <Stack spacing={3}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Food and Water
            </Typography>
            <Stack spacing={1}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!form.getField('food').value}
                    onChange={(_, checked) => {
                      form.getField('food').handleValueChange(checked);
                      form.getField('water').handleValueChange(checked);
                    }}
                    size="medium"
                  />
                }
                label="Food and Water"
              />
            </Stack>
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Mood
            </Typography>
            <Stack spacing={1}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!form.getField('mood').value}
                    onChange={(_, checked) =>
                      form.getField('mood').handleValueChange(checked)
                    }
                    size="medium"
                  />
                }
                label="Mood"
              />
            </Stack>
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Cycle
            </Typography>
            <Stack spacing={1}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!form.getField('cycleManual').value}
                    onChange={(_, checked) =>
                      form.getField('cycleManual').handleValueChange(checked)
                    }
                    size="medium"
                  />
                }
                label="Cycle"
              />
            </Stack>
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Symptoms
            </Typography>
            <Stack spacing={1}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!form.getField('symptoms').value}
                    onChange={(_, checked) =>
                      form.getField('symptoms').handleValueChange(checked)
                    }
                    size="medium"
                  />
                }
                label="Symptoms"
              />
            </Stack>
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Apple Health Data
            </Typography>
            <Stack spacing={1}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!form.getField('steps').value}
                    onChange={(_, checked) =>
                      form.getField('steps').handleValueChange(checked)
                    }
                    size="medium"
                  />
                }
                label="Steps"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={!!form.getField('exercise').value}
                    onChange={(_, checked) =>
                      form.getField('exercise').handleValueChange(checked)
                    }
                    size="medium"
                  />
                }
                label="Exercise"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={!!form.getField('sleep').value}
                    onChange={(_, checked) =>
                      form.getField('sleep').handleValueChange(checked)
                    }
                    size="medium"
                  />
                }
                label="Sleep"
              />
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Time in Daylight and Heart Rate Variability are not shown to the
              user
            </Typography>
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Daily Thoughts
            </Typography>
            <Stack spacing={1}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!form.getField('dailyThoughts').value}
                    onChange={(_, checked) =>
                      form.getField('dailyThoughts').handleValueChange(checked)
                    }
                    size="medium"
                  />
                }
                label="Daily Thoughts"
              />
            </Stack>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={form.handleSubmit} variant="contained">
          Save Changes
        </Button>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
